/**
 * Kreator modułów — kontrakt LAYOUTÓW widoków szablonu (hub Kreatora, Krok 0).
 *
 * Dwa rodzaje configów widoku encji szablonu:
 *   - widok `list`  → pełny `ListConfig` z Kreatora Listy (`list-view.ts`) — ten sam format,
 *     który trafia do kolumny `list_config` layoutu klienta (odwrócenie decyzji #11 planu
 *     Kreatora, ADR-0050). Kolumny adresowane SLUGIEM pola encji szablonu (`column.id` =
 *     slug pola) plus wbudowane `record_name` / `record_number`. Kolumna `category` jest
 *     celowo NIEDOSTĘPNA — kategorie to byt tenanta, szablon ich nie zna; wbudowane
 *     nieobecne w configu dokleja runtime klienta na końcu listy.
 *   - widok `form`  → `ModuleTemplateFormConfig` — drzewo elementów jak w builderze layoutów
 *     formularza (sekcje/karty/zakładki/pola), ale pola adresowane SLUGIEM pola encji
 *     (`fieldSlug`), nie UUID-em bytu tenanta. Instalacja tłumaczy slug→fieldId (Krok 1).
 *     Byty tenanta (visibleRoleIds, kategorie, multi-layout) świadomie poza kontraktem.
 *
 * Stary format listy (`{ columns: [{ fieldSlug, visible, sortOrder }] }`) zostaje jako typ
 * legacy + konwerter `templateListConfigFromLegacy` — używany w read-path API, mirrorze UI
 * i diffie wersji (Krok 3), żeby stare szablony i snapshoty czytały się bez błędu.
 *
 * Plan: docs/phases/MODULE_BUILDER_HUB_PLAN.md — Krok 0. ADR: docs/ADR/0048.
 */

import { z } from 'zod';

import type { ListConfig } from './list-view';
import { listConfigSchema } from './list-view';

// ============================================================================
// Stałe
// ============================================================================

/**
 * Wbudowane kolumny rekordu modułu custom dostępne w widoku listy szablonu obok slugów
 * pól encji. `category` celowo poza listą (byt tenanta — patrz nagłówek pliku).
 */
export const MODULE_TEMPLATE_BUILTIN_COLUMN_IDS = ['record_name', 'record_number'] as const;

export type ModuleTemplateBuiltinColumnId = (typeof MODULE_TEMPLATE_BUILTIN_COLUMN_IDS)[number];

/**
 * Max głębokość zagnieżdżenia drzewa układu formularza — parytet z walidatorem layoutów
 * tenanta (`layout-config-validator.ts`, DEFAULT_MAX_DEPTH).
 */
export const MODULE_TEMPLATE_FORM_MAX_DEPTH = 10;

// ============================================================================
// Stary format widoku listy (legacy — tylko odczyt + konwerter)
// ============================================================================

/** @deprecated Stary format kolumny widoku listy szablonu (MVP Kreatora). Tylko odczyt. */
export interface ModuleTemplateLegacyListColumnConfig {
  /** Slug pola encji którą pokazujemy w tej kolumnie. */
  fieldSlug: string;
  visible: boolean;
  sortOrder: number;
  width?: number;
}

/** @deprecated Stary format widoku listy szablonu. Nowe zapisy = pełny `ListConfig`. */
export interface ModuleTemplateLegacyListViewConfig {
  columns: ModuleTemplateLegacyListColumnConfig[];
}

// ============================================================================
// Układ formularza szablonu (widok `form`)
// ============================================================================

/** Zakładka w elemencie `type='tabs'` układu formularza szablonu. */
export interface ModuleTemplateFormTab {
  id: string;
  /** Slug zakładki (deep-link `?tab=...` u klienta) — unikalny w obrębie elementu tabs. */
  slug: string;
  title: string;
  icon?: string;
  validateOnChange?: boolean;
  blockOnError?: boolean;
  hidden?: boolean;
  sortOrder?: number;
  children: ModuleTemplateFormElement[];
}

/** Panel w elemencie `type='accordion'`. */
export interface ModuleTemplateFormAccordionItem {
  id: string;
  title: string;
  defaultExpanded?: boolean;
  sortOrder?: number;
  children: ModuleTemplateFormElement[];
}

/** Krok w elemencie `type='stepper'` / `type='wizard'`. */
export interface ModuleTemplateFormStep {
  id: string;
  title: string;
  icon?: string;
  validation?: boolean;
  sortOrder?: number;
  children: ModuleTemplateFormElement[];
}

/**
 * Element drzewa układu formularza szablonu — lustro `LayoutElement` z buildera layoutów
 * tenanta, z dwiema różnicami:
 *   - pola adresowane slugiem (`fieldSlug` zamiast `fieldId`) — tłumaczenie na fieldId
 *     robi instalacja / adapter buildera (Krok 1/2),
 *   - zero bytów tenanta (brak `visibleRoleIds` w zakładkach/panelach).
 */
export interface ModuleTemplateFormElement {
  /** Stabilny identyfikator elementu w drzewie (uuid z edytora) — unikalny w całym drzewie. */
  id: string;
  /** 'field' | 'section' | 'card' | 'row' | 'columns' | 'tabs' | 'accordion' | 'stepper' | 'wizard' | 'heading' | 'alert' | ... */
  type: string;
  /** Dla type='field' — slug pola encji szablonu. */
  fieldSlug?: string;
  /**
   * Alternatywny identyfikator pola — używany przez layouty syntetyzowane z definicji
   * kartoteki systemowej (buildDefaultLayoutConfig), gdzie pola adresowane są po `id`
   * z definicji (dla pól rdzeniowych id == slug). Konwerter `translateElement` obsługuje
   * oba klucze: preferuje `fieldSlug`, fallback na `fieldId`.
   */
  fieldId?: string;
  settings?: Record<string, unknown>;
  style?: Record<string, unknown>;
  /** Warunek widoczności — slug pola encji szablonu (nie id bytu tenanta). */
  conditionalFieldSlug?: string | null;
  conditionalOperator?: string | null;
  conditionalValue?: unknown;
  children?: ModuleTemplateFormElement[];
  tabs?: ModuleTemplateFormTab[];
  accordionItems?: ModuleTemplateFormAccordionItem[];
  steps?: ModuleTemplateFormStep[];
}

/** Układ formularza widoku `form` encji szablonu — root kontraktu. */
export interface ModuleTemplateFormConfig {
  elements: ModuleTemplateFormElement[];
}

// ============================================================================
// Zod schema rekurencyjna układu formularza szablonu (F1)
//
// Lustrzane do interfejsów ModuleTemplateFormElement / ModuleTemplateFormTab /
// ModuleTemplateFormAccordionItem / ModuleTemplateFormStep / ModuleTemplateFormConfig.
// Użycie z.lazy() dla rekurencji (children / tabs.children / accordionItems.children
// / steps.children mogą zawierać kolejne elementy).
// ============================================================================

const moduleTemplateFormTabSchema: z.ZodType<ModuleTemplateFormTab> = z.lazy(() =>
  z.object({
    id: z.string(),
    slug: z.string(),
    title: z.string(),
    icon: z.string().optional(),
    validateOnChange: z.boolean().optional(),
    blockOnError: z.boolean().optional(),
    hidden: z.boolean().optional(),
    sortOrder: z.number().optional(),
    children: z.array(moduleTemplateFormElementSchema),
  }),
);

const moduleTemplateFormAccordionItemSchema: z.ZodType<ModuleTemplateFormAccordionItem> = z.lazy(
  () =>
    z.object({
      id: z.string(),
      title: z.string(),
      defaultExpanded: z.boolean().optional(),
      sortOrder: z.number().optional(),
      children: z.array(moduleTemplateFormElementSchema),
    }),
);

const moduleTemplateFormStepSchema: z.ZodType<ModuleTemplateFormStep> = z.lazy(() =>
  z.object({
    id: z.string(),
    title: z.string(),
    icon: z.string().optional(),
    validation: z.boolean().optional(),
    sortOrder: z.number().optional(),
    children: z.array(moduleTemplateFormElementSchema),
  }),
);

const moduleTemplateFormElementSchema: z.ZodType<ModuleTemplateFormElement> = z.lazy(() =>
  z.object({
    id: z.string(),
    type: z.string(),
    fieldSlug: z.string().optional(),
    fieldId: z.string().optional(),
    settings: z.record(z.string(), z.unknown()).optional(),
    style: z.record(z.string(), z.unknown()).optional(),
    conditionalFieldSlug: z.string().nullable().optional(),
    conditionalOperator: z.string().nullable().optional(),
    conditionalValue: z.unknown().optional(),
    children: z.array(moduleTemplateFormElementSchema).optional(),
    tabs: z.array(moduleTemplateFormTabSchema).optional(),
    accordionItems: z.array(moduleTemplateFormAccordionItemSchema).optional(),
    steps: z.array(moduleTemplateFormStepSchema).optional(),
  }),
);

/** Zod schema rekurencyjna layoutu formularza szablonu — root kontraktu. */
export const moduleTemplateFormConfigSchema: z.ZodType<ModuleTemplateFormConfig> = z.object({
  elements: z.array(moduleTemplateFormElementSchema),
});

// ============================================================================
// Konwerter starego formatu listy → pełny ListConfig
// ============================================================================

/**
 * Czy wartość to widok listy w STARYM formacie (kolumny z `fieldSlug`)?
 *
 * Detekcja po kluczach: nowy format po przejściu przez Zod ma ZAWSZE wymagane klucze
 * `defaultSort` + `defaultFilters`; stary nigdy ich nie miał (same `columns`).
 */
export function isLegacyTemplateListConfig(
  value: unknown,
): value is ModuleTemplateLegacyListViewConfig {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const obj = value as Record<string, unknown>;
  if (!Array.isArray(obj.columns)) return false;
  if ('defaultSort' in obj || 'defaultFilters' in obj) return false;
  return obj.columns.every(
    (col) =>
      !!col &&
      typeof col === 'object' &&
      typeof (col as { fieldSlug?: unknown }).fieldSlug === 'string',
  );
}

/**
 * Konwertuje stary format widoku listy szablonu na pełny `ListConfig`:
 *   - `fieldSlug` → `column.id`, `sortOrder` → `order` (renumeracja po stabilnym sortowaniu),
 *   - syntetyzuje wymagane pola: `width` (brak → 'auto', jak runtime ConfigurableTable),
 *     `defaultSort: null`, `defaultFilters: []`.
 *
 * Wynik przechodzi przez `listConfigSchema` (normalizacja defaultów: formattingRules,
 * quickFilters, defaultViewMode) — DETERMINISTYCZNY, bo na nim opiera się porównanie
 * snapshotów (dedup zapisu, Krok 3 diff, odcisk „nietknięty" z Kroku 1).
 */
export function templateListConfigFromLegacy(
  legacy: ModuleTemplateLegacyListViewConfig,
): ListConfig {
  const columns = [...legacy.columns]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((col, index) => ({
      id: col.fieldSlug,
      visible: col.visible,
      width: col.width ?? ('auto' as const),
      order: index,
    }));
  const converted = { columns, defaultSort: null, defaultFilters: [] };
  const parsed = listConfigSchema.safeParse(converted);
  // safeParse nie powinien się nie powieść (kształt budujemy sami) — fallback defensywny,
  // żeby read-path nigdy nie wywrócił odczytu starego szablonu.
  return parsed.success ? parsed.data : (converted as unknown as ListConfig);
}

/**
 * Normalizuje config widoku listy do nowego formatu niezależnie od pochodzenia:
 * legacy → konwersja, nowy format → bez zmian, brak → undefined. Używane w read-path
 * API, mirrorze walidacji UI i porównaniach snapshotów.
 */
export function ensureTemplateListConfig(value: unknown): ListConfig | undefined {
  if (value == null) return undefined;
  if (isLegacyTemplateListConfig(value)) return templateListConfigFromLegacy(value);
  return value as ListConfig;
}

// ============================================================================
// Reguły semantyczne configów widoków (wspólne dla Zod backendu i mirroru UI)
// ============================================================================

/** Problem w configu widoku listy szablonu. */
export type TemplateListConfigProblem =
  | { kind: 'no_columns' }
  | { kind: 'unknown_column'; columnId: string };

/** Opcje kolektora problemów configów widoku. */
export interface TemplateViewCollectorOptions {
  /**
   * Gdy `true` pomija sprawdzanie ISTNIENIA pól w configu widoku (`unknown_column` /
   * `unknown_field` / `unknown_conditionalField`). Stosowane dla encji-odwołania
   * (`ddlStrategy === 'reference_to_system_entity'`), której pola żyją w kartotece
   * systemowej (poza modelem szablonu). Pozostałe walidacje (min. 1 kolumna, duplikaty,
   * głębokość) zostają bez zmian.
   *
   * Default: `false` — zachowanie bez zmian dla istniejących callerów.
   */
  allowUnknownFields?: boolean;
  /**
   * Gdy `true` formularz musi zawierać co najmniej jedno pole (problem `no_fields`). Stosowane
   * dla układów z biblioteki kartoteki (panel ITE) — jak kreator formularza u klienta, który
   * nie pozwala zapisać pustego układu. Default `false`: szablony modułów mogą mieć pusty
   * formularz domyślny.
   */
  requireField?: boolean;
}

/**
 * Sprawdza widok listy (nowy format) przeciw polom encji: każda kolumna musi wskazywać
 * slug pola encji albo wbudowaną (`record_name`/`record_number`). Zwraca listę problemów
 * (pusta = OK). Jedno zgłoszenie per nieznany id kolumny.
 *
 * Gdy `options.allowUnknownFields === true` pomija sprawdzenie `unknown_column`
 * (stosowane dla encji-odwołania).
 */
export function collectTemplateListConfigProblems(
  config: ListConfig,
  knownFieldSlugs: ReadonlySet<string>,
  options?: TemplateViewCollectorOptions,
): TemplateListConfigProblem[] {
  const problems: TemplateListConfigProblem[] = [];
  // Guard na uszkodzony JSONB — mirror w edytorze liczy się na każdy stan, nie może rzucać.
  const columns = Array.isArray(config.columns) ? config.columns : [];
  if (columns.length === 0) {
    problems.push({ kind: 'no_columns' });
  }
  if (options?.allowUnknownFields) {
    return problems;
  }
  const reported = new Set<string>();
  for (const column of columns) {
    const known =
      knownFieldSlugs.has(column.id) ||
      (MODULE_TEMPLATE_BUILTIN_COLUMN_IDS as readonly string[]).includes(column.id);
    if (!known && !reported.has(column.id)) {
      problems.push({ kind: 'unknown_column', columnId: column.id });
      reported.add(column.id);
    }
  }
  return problems;
}

/** Problem w układzie formularza szablonu. */
export type TemplateFormConfigProblem =
  | { kind: 'field_without_slug'; elementId: string }
  | { kind: 'unknown_field'; slug: string }
  | { kind: 'duplicate_element_id'; id: string }
  | { kind: 'too_deep'; depth: number }
  | { kind: 'no_fields' };

/**
 * Sprawdza drzewo układu formularza przeciw polom encji szablonu:
 *   - element `type='field'` musi mieć `fieldSlug` wskazujący istniejące pole encji,
 *   - `conditionalFieldSlug` (gdy ustawiony) też musi wskazywać pole encji,
 *   - `id` elementów (w tym zakładek/paneli/kroków) unikalne w całym drzewie,
 *   - max głębokość zagnieżdżenia (parytat z walidatorem layoutów tenanta).
 *
 * `fieldSlug` NIE musi być unikalny — to samo pole może się pojawić w drzewie
 * wielokrotnie (ujednolicone z `layout-config-validator.ts`, który od tej samej
 * zmiany akceptuje duplikaty `fieldId` w layoutach tenanta; szablon modułu
 * własnego nie powinien być bardziej restrykcyjny niż layout, który z niego
 * powstaje).
 *
 * Zwraca listę problemów (pusta = OK); duplikaty `id` zgłaszane raz per id.
 *
 * Gdy `options.allowUnknownFields === true` pomija sprawdzenie `unknown_field`
 * (stosowane dla encji-odwołania).
 */
export function collectTemplateFormConfigProblems(
  config: ModuleTemplateFormConfig,
  knownFieldSlugs: ReadonlySet<string>,
  maxDepth: number = MODULE_TEMPLATE_FORM_MAX_DEPTH,
  options?: TemplateViewCollectorOptions,
): TemplateFormConfigProblem[] {
  const problems: TemplateFormConfigProblem[] = [];
  const seenIds = new Set<string>();
  const duplicateIds = new Set<string>();
  const unknownFieldSlugs = new Set<string>();
  let maxFoundDepth = 0;
  let hasField = false;

  const registerId = (id: string) => {
    if (seenIds.has(id)) duplicateIds.add(id);
    else seenIds.add(id);
  };

  const registerFieldRef = (slug: string) => {
    if (!knownFieldSlugs.has(slug)) unknownFieldSlugs.add(slug);
  };

  const walk = (element: ModuleTemplateFormElement, depth: number) => {
    maxFoundDepth = Math.max(maxFoundDepth, depth);
    if (depth > maxDepth) return; // nie schodzimy głębiej — problem i tak zgłosimy raz

    registerId(element.id);

    if (element.type === 'field') {
      if (!element.fieldSlug) {
        problems.push({ kind: 'field_without_slug', elementId: element.id });
      } else {
        hasField = true;
        registerFieldRef(element.fieldSlug);
      }
    }
    if (element.conditionalFieldSlug) {
      registerFieldRef(element.conditionalFieldSlug);
    }

    for (const child of element.children ?? []) walk(child, depth + 1);
    for (const tab of element.tabs ?? []) {
      registerId(tab.id);
      for (const child of tab.children) walk(child, depth + 1);
    }
    for (const item of element.accordionItems ?? []) {
      registerId(item.id);
      for (const child of item.children) walk(child, depth + 1);
    }
    for (const step of element.steps ?? []) {
      registerId(step.id);
      for (const child of step.children) walk(child, depth + 1);
    }
  };

  // Guard na uszkodzony JSONB — jak w kolektorze listy.
  for (const element of Array.isArray(config.elements) ? config.elements : []) walk(element, 1);

  if (maxFoundDepth > maxDepth) problems.push({ kind: 'too_deep', depth: maxFoundDepth });
  if (options?.requireField && !hasField) problems.push({ kind: 'no_fields' });
  if (!options?.allowUnknownFields) {
    for (const slug of unknownFieldSlugs) problems.push({ kind: 'unknown_field', slug });
  }
  for (const id of duplicateIds) problems.push({ kind: 'duplicate_element_id', id });

  return problems;
}
